import {
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  Save,
  Loader2,
  Camera,
  Palette,
  QrCode,
  Copy,
  Check,
  Image as ImageIcon,
  Star,
  Link2,
  AlertCircle,
  Layers,
  Eye,
} from 'lucide-react'
import { QRCodeSVG } from 'qrcode.react'

import api from '../../lib/axios'
import { THEMES, FONTS } from '../../lib/themeConfig'
import { normalizeSettings } from '../../lib/normalizeSettings'
import {
  REVIEW_DESIGNS,
  DEFAULT_TAP_TEXT,
  getReviewDesign,
} from '../../lib/googleReviewDesigns'
import GoogleReviewRenderer from '../../components/layout/GoogleReviewRenderer'

/* =========================================================
   DEFAULT SETTINGS
========================================================= */

const DEFAULT_SETTINGS = {
  theme: 'classic',
  font: 'inter',
  buttonShape: 'rounded',
  buttonStyle: 'solid',
  design: 'card',
  accentColor: '#111827',
  showQr: true,
}

const inputCls =
  'w-full px-3 py-2 bg-slate-50 rounded-lg text-sm border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#006b5f]/20'

/* =========================================================
   HELPERS
========================================================= */

function slugify(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60)
}

function isValidUrl(value) {
  try {
    const url = new URL(value)

    return (
      url.protocol === 'http:' ||
      url.protocol === 'https:'
    )
  } catch {
    return false
  }
}

function isGoogleHost(host) {
  return /(^|\.)google\.(com|[a-z]{2})(\.[a-z]{2})?$/.test(
    host.toLowerCase()
  )
}

// Lien court Google Maps (maps.app.goo.gl, goo.gl, g.co)
function isGoogleMapsShortLink(value) {
  if (!value) return false

  try {
    const url = new URL(value)

    if (url.protocol !== 'https:') return false

    const host = url.hostname.toLowerCase()

    return (
      host === 'maps.app.goo.gl' ||
      host === 'goo.gl' ||
      host === 'g.co'
    )
  } catch {
    return false
  }
}

// Lien long Google Maps
function isGoogleMapsLongLink(value) {
  if (!value) return false

  try {
    const url = new URL(value)

    if (url.protocol !== 'https:') return false

    const host = url.hostname.toLowerCase()

    return (
      isGoogleHost(host) &&
      host !== 'search.google.com'
    )
  } catch {
    return false
  }
}

// Lien direct « Rédiger un avis »
function isGoogleReviewLink(value) {
  if (!value) return false

  try {
    const url = new URL(value)

    return (
      url.protocol === 'https:' &&
      url.hostname.toLowerCase() ===
        'search.google.com' &&
      url.pathname === '/local/writereview' &&
      Boolean(url.searchParams.get('placeid'))
    )
  } catch {
    return false
  }
}

function getReviewPlaceId(value) {
  if (!isGoogleReviewLink(value)) return null

  try {
    return new URL(value).searchParams.get('placeid') || null
  } catch {
    return null
  }
}

function isValidRating(value) {
  const v = String(value ?? '').trim()

  if (!v) return true

  if (!/^\d(\.\d{1,2})?$/.test(v)) return false

  const n = Number(v)

  return n >= 0 && n <= 5
}

function isValidReviewCount(value) {
  const v = String(value ?? '').trim()

  return !v || /^\d+$/.test(v)
}

// Un vrai Place ID commence par ChIJ (un FTID du type 0x1234:0xabcd n'en est pas un).
function extractRealPlaceId(value) {
  if (!value) return null

  try {
    const url = new URL(value)

    const placeId =
      url.searchParams.get('place_id') ||
      url.searchParams.get('query_place_id')

    if (placeId && /^ChIJ[a-zA-Z0-9_-]+$/.test(placeId)) {
      return placeId
    }

    const match = value.match(/ChIJ[a-zA-Z0-9_-]+/)

    return match ? match[0] : null
  } catch {
    return null
  }
}

function buildReviewUrl(placeId) {
  if (!placeId) return ''

  return (
    'https://search.google.com/local/writereview?placeid=' +
    encodeURIComponent(placeId)
  )
}

function ColorInput({ label, value, onChange }) {
  const safeValue = /^#[0-9a-f]{6}$/i.test(value || '')
    ? value
    : '#000000'

  return (
    <div>
      <label className="block text-[11px] font-semibold text-navy mb-1">
        {label}
      </label>

      <div className="flex gap-2">
        <input
          type="color"
          value={safeValue}
          onChange={(event) => onChange(event.target.value)}
          className="w-10 h-9 rounded-lg border border-slate-200 cursor-pointer shrink-0"
        />

        <input
          type="text"
          value={value || ''}
          onChange={(event) => onChange(event.target.value)}
          className="flex-1 min-w-0 px-2 py-2 bg-slate-50 rounded-lg text-xs border border-slate-200 font-mono focus:outline-none focus:ring-2 focus:ring-[#006b5f]/20"
        />
      </div>
    </div>
  )
}

/* =========================================================
   COMPONENT
========================================================= */

export default function GoogleReviewPageEditor() {
  const { id } = useParams()
  const navigate = useNavigate()

  const isNew = !id || id === 'new'

  const [isLoading, setIsLoading] = useState(!isNew)
  const [isSaving, setIsSaving] = useState(false)
  const [resolvingMap, setResolvingMap] = useState(false)

  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  const [showQrModal, setShowQrModal] = useState(false)
  const [copied, setCopied] = useState(false)
  const [uploadingLogo, setUploadingLogo] = useState(false)
  const [activeTab, setActiveTab] = useState('content')
  const [loadFailed, setLoadFailed] = useState(false)
  // < lg : un seul panneau à la fois (éditer OU aperçu). >= lg : les deux côte à côte.
  const [mobileView, setMobileView] = useState('edit')

  const logoInputRef = useRef(null)
  const debounceRef = useRef(null)
  const saveRef = useRef(null)
  const savingRef = useRef(false)

  const createdIdRef = useRef(null)
  const createdOnceRef = useRef(false)

  const flashTimerRef = useRef(null)
  const copyTimerRef = useRef(null)
  const placeIdRef = useRef(null)

  const [page, setPage] = useState(() => ({
    title: 'Mon Établissement',
    slug: '',
    logo: '',
    is_published: true,
    settings: { ...DEFAULT_SETTINGS },
    googleUrl: '',
    rating: '',
    reviewCount: '',
    ctaLabel: 'Laisser un avis Google',
    tapText: DEFAULT_TAP_TEXT,
  }))

  const flash = useCallback((message) => {
    setSuccess(message)

    if (flashTimerRef.current) {
      clearTimeout(flashTimerRef.current)
    }

    flashTimerRef.current = setTimeout(() => {
      setSuccess('')
      flashTimerRef.current = null
    }, 3000)
  }, [])

  // Sur mobile, un message d'erreur est dans le panneau « Éditer » : on y revient pour le voir.
  useEffect(() => {
    if (error) setMobileView('edit')
  }, [error])

  /* ---------- Chargement de la page ---------- */

  const loadPage = useCallback(async () => {
    if (!id || id === 'new') {
      setIsLoading(false)
      setLoadFailed(false)
      return
    }

    setIsLoading(true)
    setError('')
    setLoadFailed(false)

    try {
      const { data } = await api.get(`/pages/${id}`)
      const p = data?.data ?? data

      if (!p || typeof p !== 'object') {
        throw new Error('Les données de la page sont invalides.')
      }

      const normalized = normalizeSettings(p.settings) || {}

      const {
        googleUrl,
        rating,
        reviewCount,
        ctaLabel,
        tapText,
        ...visual
      } = normalized

      setPage({
        title: p.title || 'Mon Établissement',
        slug: p.slug || '',
        logo: p.avatar || '',
        is_published: Boolean(p.is_published),

        settings: {
          ...DEFAULT_SETTINGS,
          ...visual,
        },

        googleUrl: googleUrl || '',

        rating:
          rating !== undefined && rating !== null
            ? String(rating)
            : '',

        reviewCount:
          reviewCount !== undefined && reviewCount !== null
            ? String(reviewCount)
            : '',

        ctaLabel: ctaLabel || 'Laisser un avis Google',
        tapText: tapText || DEFAULT_TAP_TEXT,
      })

      placeIdRef.current = getReviewPlaceId(googleUrl)
    } catch (err) {
      console.error('Erreur loadPage:', err)

      setLoadFailed(true)

      setError(
        err?.response?.data?.message ||
          err?.message ||
          'Impossible de charger la page.'
      )
    } finally {
      setIsLoading(false)
    }
  }, [id])

  useEffect(() => {
    if (isNew) {
      setIsLoading(false)
      setLoadFailed(false)
      return
    }

    loadPage()
  }, [isNew, loadPage])

  // Nettoyage des timers
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
        debounceRef.current = null
      }

      if (flashTimerRef.current) {
        clearTimeout(flashTimerRef.current)
        flashTimerRef.current = null
      }

      if (copyTimerRef.current) {
        clearTimeout(copyTimerRef.current)
        copyTimerRef.current = null
      }
    }
  }, [])

  /* ---------- Valeurs dérivées ---------- */

  const settings = page.settings || DEFAULT_SETTINGS

  const currentDesign =
    getReviewDesign(settings.design) ||
    REVIEW_DESIGNS?.[0] ||
    {}

  const publicUrl =
    `${window.location.origin}/avis/${page.slug || 'votre-slug'}`

  const currentGoogleUrl = String(page.googleUrl || '').trim()

  const urlLooksWrong =
    currentGoogleUrl.length > 0 &&
    !isValidUrl(currentGoogleUrl)

  const isShortLink = isGoogleMapsShortLink(currentGoogleUrl)
  const isReviewLink = isGoogleReviewLink(currentGoogleUrl)

  const buildSettings = (googleUrlOverride = null) => ({
    ...page.settings,

    googleUrl:
      googleUrlOverride !== null
        ? String(googleUrlOverride).trim()
        : String(page.googleUrl || '').trim(),

    rating: page.rating,
    reviewCount: page.reviewCount,
    ctaLabel: page.ctaLabel,
    tapText: page.tapText,
  })

  /* ---------- Résolution du lien Google Maps ---------- */

  const resolveGoogleMapsLink = async (googleMapsUrl) => {
    const value = String(googleMapsUrl || '').trim()

    if (!value) return { success: false }

    setResolvingMap(true)
    setError('')

    try {
      const response = await api.post('/google/resolve-maps', {
        url: value,
      })

      const payload =
        response?.data?.data ??
        response?.data ??
        {}

      const reviewUrl =
        payload.reviewUrl ||
        payload.review_url ||
        ''

      const placeId =
        payload.placeId ||
        payload.place_id ||
        ''

      const placeName =
        payload.name ||
        payload.displayName ||
        payload.display_name ||
        ''

      const address =
        payload.address ||
        payload.formattedAddress ||
        ''

      const rating = payload.rating

      const reviewCount =
        payload.reviewCount ??
        payload.userRatingCount

      if (!reviewUrl) {
        throw new Error(
          'Le serveur n’a pas retourné de lien direct pour rédiger un avis.'
        )
      }

      if (!isValidUrl(reviewUrl)) {
        throw new Error(
          'Le lien d’avis retourné par Google est invalide.'
        )
      }

      if (!isGoogleReviewLink(reviewUrl)) {
        throw new Error(
          'Le serveur a retourné un lien qui ne permet pas de rédiger directement un avis Google.'
        )
      }

      const newPlaceId = getReviewPlaceId(reviewUrl)

      const placeChanged =
        Boolean(placeIdRef.current) &&
        placeIdRef.current !== newPlaceId

      placeIdRef.current = newPlaceId

      setPage((previous) => ({
        ...previous,

        googleUrl: reviewUrl,

        title:
          previous.title &&
          previous.title !== 'Mon Établissement'
            ? previous.title
            : placeName || previous.title,

        rating:
          rating !== undefined && rating !== null
            ? String(rating)
            : placeChanged
              ? ''
              : previous.rating,

        reviewCount:
          reviewCount !== undefined && reviewCount !== null
            ? String(reviewCount)
            : placeChanged
              ? ''
              : previous.reviewCount,
      }))

      flash(
        placeName
          ? `✓ ${placeName} — lien direct généré`
          : '✓ Lien direct « Rédiger un avis » généré'
      )

      return {
        success: true,
        reviewUrl,
        placeId,
        name: placeName,
        address,
        rating,
        reviewCount,
      }
    } catch (err) {
      console.error('Erreur resolveGoogleMapsLink:', err)

      const data = err?.response?.data

      const backendMessage =
        data?.error ||
        data?.message ||
        data?.errors?.url?.[0]

      setError(
        backendMessage ||
          err?.message ||
          'Impossible de résoudre ce lien Google Maps.'
      )

      return { success: false }
    } finally {
      setResolvingMap(false)
    }
  }

  const handleGoogleUrlChange = (value) => {
    setError('')

    setPage((previous) => ({
      ...previous,
      googleUrl: value,
    }))

    const trimmed = String(value || '').trim()

    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
      debounceRef.current = null
    }

    if (!trimmed) return

    if (isGoogleReviewLink(trimmed)) {
      placeIdRef.current = getReviewPlaceId(trimmed)
      return
    }

    if (
      isGoogleMapsShortLink(trimmed) &&
      trimmed.length > 25
    ) {
      debounceRef.current = setTimeout(() => {
        resolveGoogleMapsLink(trimmed)
      }, 700)

      return
    }

    if (isGoogleMapsLongLink(trimmed)) {
      const placeId = extractRealPlaceId(trimmed)

      if (placeId) {
        const reviewUrl = buildReviewUrl(placeId)

        const placeChanged =
          Boolean(placeIdRef.current) &&
          placeIdRef.current !== placeId

        placeIdRef.current = placeId

        setPage((previous) => ({
          ...previous,
          googleUrl: reviewUrl,

          ...(placeChanged
            ? { rating: '', reviewCount: '' }
            : {}),
        }))

        flash('✓ Lien direct « Rédiger un avis » généré')
        return
      }

      if (trimmed.length > 30) {
        debounceRef.current = setTimeout(() => {
          resolveGoogleMapsLink(trimmed)
        }, 700)
      }
    }
  }

  /* ---------- Sauvegarde ---------- */

  const performSave = async () => {
    if (loadFailed) {
      setError(
        'La page n’a pas pu être chargée : rechargez-la avant d’enregistrer.'
      )
      return
    }

    if (uploadingLogo) {
      setError(
        'Un envoi d’image est en cours : patientez avant d’enregistrer.'
      )
      return
    }

    if (resolvingMap) {
      setError(
        'La recherche Google Maps est en cours. Patientez avant d’enregistrer.'
      )
      return
    }

    setError('')
    setSuccess('')

    let googleUrl = String(page.googleUrl || '').trim()

    if (
      isGoogleMapsShortLink(googleUrl) ||
      (
        isGoogleMapsLongLink(googleUrl) &&
        !isGoogleReviewLink(googleUrl)
      )
    ) {
      const result = await resolveGoogleMapsLink(googleUrl)

      if (!result?.success) {
        setActiveTab('content')
        return
      }

      googleUrl = result.reviewUrl || ''

      if (!googleUrl) {
        setError('Impossible de générer le lien direct Google.')
        setActiveTab('content')
        return
      }
    }

    if (!googleUrl) {
      setError('Ajoutez le lien Google Maps de votre établissement.')
      setActiveTab('content')
      return
    }

    if (!isValidUrl(googleUrl)) {
      setError('Le lien Google est invalide.')
      setActiveTab('content')
      return
    }

    if (!isGoogleReviewLink(googleUrl)) {
      setError(
        'Le lien Google doit permettre au client de rédiger directement un avis.'
      )
      setActiveTab('content')
      return
    }

    if (!isValidRating(page.rating)) {
      setError(
        'La note doit être un nombre entre 0 et 5 (ex. : 4.8), ou rester vide.'
      )
      setActiveTab('content')
      return
    }

    if (!isValidReviewCount(page.reviewCount)) {
      setError(
        'Le nombre d’avis doit être un nombre entier, ou rester vide.'
      )
      setActiveTab('content')
      return
    }

    const targetId = isNew ? createdIdRef.current : id

    if (isNew && createdOnceRef.current && !targetId) {
      setError(
        'La page a déjà été créée, mais le serveur n’a pas renvoyé son identifiant. Rouvrez-la depuis la liste pour la modifier.'
      )
      return
    }

    setIsSaving(true)

    try {
      const finalSlug =
        String(page.slug || '').trim() ||
        slugify(page.title)

      const payload = {
        type: 'google_review',

        title:
          String(page.title || '').trim() ||
          'Avis Google',

        slug: finalSlug || undefined,

        is_published: Boolean(page.is_published),

        avatar: page.logo || null,

        settings: buildSettings(googleUrl),
      }

      if (!targetId) {
        const { data } = await api.post('/pages', payload)

        const newId =
          data?.data?.id ||
          data?.id

        createdOnceRef.current = true
        createdIdRef.current = newId || null

        if (newId) {
          navigate(`/app/reviews/${newId}/edit`, {
            replace: true,
          })
        } else {
          setPage((previous) => ({
            ...previous,
            googleUrl,
          }))

          flash('✓ Page créée')
        }

        return
      }

      await api.put(`/pages/${targetId}`, payload)

      setPage((previous) => ({
        ...previous,
        googleUrl,
      }))

      placeIdRef.current = getReviewPlaceId(googleUrl)

      flash('✓ Page sauvegardée')
    } catch (err) {
      console.error(
        'ERREUR SAUVEGARDE:',
        err?.response?.data || err
      )

      const data = err?.response?.data

      const validationErrors = data?.errors
        ? Object.values(data.errors).flat().join(' • ')
        : ''

      setError(
        data?.error ||
          data?.message ||
          validationErrors ||
          err?.message ||
          'Erreur lors de la sauvegarde.'
      )
    } finally {
      setIsSaving(false)
    }
  }

  // Verrou anti double-sauvegarde + Ctrl/Cmd + S
  const handleSave = async () => {
    if (savingRef.current) return

    savingRef.current = true

    try {
      await performSave()
    } finally {
      savingRef.current = false
    }
  }

  saveRef.current = handleSave

  useEffect(() => {
    const handleKeyboard = (event) => {
      if (
        (event.ctrlKey || event.metaKey) &&
        event.key.toLowerCase() === 's'
      ) {
        event.preventDefault()
        saveRef.current?.()
      }
    }

    window.addEventListener('keydown', handleKeyboard)

    return () => {
      window.removeEventListener('keydown', handleKeyboard)
    }
  }, [])

  /* ---------- Logo ---------- */

  const handleLogoUpload = async (event) => {
    const file = event.target.files?.[0]

    event.target.value = ''

    if (!file) return

    if (!file.type.startsWith('image/')) {
      setError('Veuillez sélectionner un fichier image.')
      return
    }

    setUploadingLogo(true)
    setError('')

    try {
      const formData = new FormData()

      formData.append('file', file)
      formData.append('type', 'avatar')

      const { data } = await api.post('/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })

      const logoUrl =
        data?.url ||
        data?.data?.url ||
        ''

      if (!logoUrl) {
        throw new Error(
          'Le serveur n’a pas retourné l’URL du logo.'
        )
      }

      setPage((previous) => ({
        ...previous,
        logo: logoUrl,
      }))

      flash('✓ Logo ajouté')
    } catch (err) {
      console.error('Erreur upload logo:', err)

      setError(
        err?.response?.data?.message ||
          err?.message ||
          'Erreur lors de l’upload du logo.'
      )
    } finally {
      setUploadingLogo(false)
    }
  }

  /* ---------- Copier le lien ---------- */

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl)

      setCopied(true)

      if (copyTimerRef.current) {
        clearTimeout(copyTimerRef.current)
      }

      copyTimerRef.current = setTimeout(() => {
        setCopied(false)
        copyTimerRef.current = null
      }, 2000)
    } catch (err) {
      console.error('Erreur copie:', err)
      setError(
        'Impossible de copier le lien. Vérifiez les permissions du navigateur.'
      )
    }
  }

  const updateSettings = (key, value) => {
    setPage((previous) => ({
      ...previous,

      settings: {
        ...previous.settings,
        [key]: value,
      },
    }))
  }

  const chooseDesign = (design) => {
    setPage((previous) => ({
      ...previous,

      settings: {
        ...previous.settings,
        design: design.id,
        accentColor:
          design.accent ||
          previous.settings.accentColor,
      },
    }))
  }

  /* ---------- États de chargement ---------- */

  if (isLoading) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-[#f9f9ff]">
        <Loader2 className="w-8 h-8 text-[#006b5f] animate-spin" />
      </div>
    )
  }

  if (loadFailed) {
    return (
      <div className="min-h-dvh flex flex-col items-center justify-center gap-3 bg-[#f9f9ff] px-6 text-center text-sm">
        <p className="text-red-600">
          {error || 'Impossible de charger la page.'}
        </p>

        <div className="flex gap-2">
          <button
            onClick={loadPage}
            className="px-3 py-1.5 bg-[#006b5f] hover:bg-[#005a50] text-white rounded-lg font-semibold text-xs"
          >
            Réessayer
          </button>

          <button
            onClick={() => navigate('/app/pages')}
            className="px-3 py-1.5 text-slate-500 hover:text-navy rounded-lg text-xs"
          >
            Retour
          </button>
        </div>
      </div>
    )
  }

  /* ---------- Rendu ---------- */

  return (
    <div className="min-h-dvh bg-[#f9f9ff] flex">
      {/* ═══ PANNEAU D'ÉDITION ═══ */}
      <aside className={`${mobileView === 'preview' ? 'hidden' : 'flex'} lg:flex w-full lg:w-[400px] bg-white lg:border-r border-slate-100 flex-col fixed left-0 top-0 bottom-[calc(3.5rem+env(safe-area-inset-bottom))] lg:bottom-0 z-20`}>
        {/* En-tête */}
        <div className="p-3 sm:p-4 border-b border-slate-100 flex items-center justify-between gap-2">
          <button
            onClick={() => navigate('/app/pages')}
            className="flex items-center gap-2 text-sm text-slate-500 hover:text-navy"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour
          </button>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowQrModal(true)}
              className="p-2 text-slate-500 hover:text-[#006b5f] rounded-lg hover:bg-slate-50"
              title="QR Code"
              aria-label="QR Code"
            >
              <QrCode className="w-4 h-4" />
            </button>

            <button
              onClick={handleSave}
              disabled={isSaving || resolvingMap || uploadingLogo}
              className="hidden lg:flex items-center gap-2 px-3 py-1.5 bg-[#006b5f] hover:bg-[#005a50] text-white rounded-lg font-semibold text-xs transition-colors disabled:opacity-60"
            >
              {isSaving ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <Save className="w-3 h-3" />
              )}
              Enregistrer
            </button>
          </div>
        </div>

        {/* Onglets */}
        <div className="flex border-b border-slate-100 px-2 sm:px-3 gap-1 overflow-x-auto scrollbar-thin">
          {[
            { id: 'content', label: 'Contenu', icon: Star },
            { id: 'design', label: 'Design', icon: Palette },
          ].map((tab) => {
            const Icon = tab.icon

            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-3 py-3 text-xs font-semibold border-b-2 whitespace-nowrap shrink-0 transition-colors ${
                  activeTab === tab.id
                    ? 'border-[#006b5f] text-[#006b5f]'
                    : 'border-transparent text-slate-500'
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {tab.label}
              </button>
            )
          })}
        </div>

        {/* Messages */}
        {(error || success) && (
          <div className="p-3 space-y-2">
            {error && (
              <div className="p-3 bg-red-50 border border-red-100 rounded-lg text-xs text-red-600 break-words">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              </div>
            )}

            {success && (
              <div className="p-3 bg-teal-50 border border-teal-100 rounded-lg text-xs text-teal-700">
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4" />
                  <span>{success}</span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Contenu */}
        <div className="flex-1 overflow-y-auto p-4 sm:px-[max(1rem,calc((100%-42rem)/2))] lg:px-4 space-y-4">
          {activeTab === 'content' && (
            <>
              {/* Logo + nom */}
              <div className="flex items-center gap-4">
                <div className="relative">
                  <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center overflow-hidden">
                    {page.logo ? (
                      <img
                        src={page.logo}
                        alt="Logo de l’établissement"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <ImageIcon className="w-5 h-5 text-slate-400" />
                    )}
                  </div>

                  <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="hidden"
                  />

                  <button
                    onClick={() => logoInputRef.current?.click()}
                    disabled={uploadingLogo}
                    className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-white shadow flex items-center justify-center disabled:opacity-50"
                    title="Ajouter un logo"
                  >
                    {uploadingLogo ? (
                      <Loader2 className="w-3 h-3 animate-spin text-[#006b5f]" />
                    ) : (
                      <Camera className="w-3 h-3 text-[#006b5f]" />
                    )}
                  </button>
                </div>

                <div className="flex-1 min-w-0">
                  <label className="block text-xs font-semibold text-navy mb-1">
                    Nom de l'établissement
                  </label>

                  <input
                    type="text"
                    value={page.title}
                    onChange={(event) =>
                      setPage((previous) => ({
                        ...previous,
                        title: event.target.value,
                      }))
                    }
                    className={inputCls}
                  />
                </div>
              </div>

              {/* Slug */}
              <div>
                <label className="block text-xs font-semibold text-navy mb-1">
                  Lien de la page
                </label>

                <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-lg border border-slate-200 text-xs">
                  <span className="text-slate-400 shrink-0">
                    tapio.link/avis/
                  </span>

                  <input
                    type="text"
                    value={page.slug}
                    onChange={(event) =>
                      setPage((previous) => ({
                        ...previous,
                        slug: event.target.value
                          .toLowerCase()
                          .replace(/[^a-z0-9-]/g, '-'),
                      }))
                    }
                    placeholder={slugify(page.title) || 'mon-etablissement'}
                    className="flex-1 min-w-0 bg-transparent outline-none text-navy"
                  />
                </div>
              </div>

              {/* Publiée */}
              <label className="flex items-center gap-2 text-xs font-semibold text-navy cursor-pointer">
                <input
                  type="checkbox"
                  checked={page.is_published}
                  onChange={(event) =>
                    setPage((previous) => ({
                      ...previous,
                      is_published: event.target.checked,
                    }))
                  }
                  className="w-4 h-4 accent-[#006b5f]"
                />
                Page publiée
              </label>

              {/* Lien Google Maps */}
              <div>
                <label className="block text-xs font-semibold text-navy mb-1">
                  <span className="inline-flex items-center gap-1.5">
                    <Link2 className="w-3.5 h-3.5" />
                    Lien Google Maps de l'établissement
                  </span>
                </label>

                <div className="relative">
                  <input
                    type="text"
                    value={page.googleUrl}
                    onChange={(event) =>
                      handleGoogleUrlChange(event.target.value)
                    }
                    placeholder="https://maps.app.goo.gl/..."
                    disabled={resolvingMap}
                    className={`${inputCls} ${
                      urlLooksWrong ? '!border-red-300' : ''
                    } ${resolvingMap ? 'pr-10' : ''}`}
                  />

                  {resolvingMap && (
                    <div className="absolute right-2 top-1/2 -translate-y-1/2">
                      <Loader2 className="w-4 h-4 animate-spin text-[#006b5f]" />
                    </div>
                  )}
                </div>

                {resolvingMap && (
                  <div className="mt-2 p-2.5 bg-teal-50 border border-teal-100 rounded-lg">
                    <p className="text-[11px] text-[#006b5f] flex items-center gap-2">
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      Recherche de votre établissement sur Google…
                    </p>
                    <p className="text-[10px] text-teal-600 mt-1 ml-5">
                      Génération du lien direct « Rédiger un avis »…
                    </p>
                  </div>
                )}

                {urlLooksWrong && !resolvingMap && (
                  <p className="text-[11px] text-red-500 mt-1 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    Le lien saisi n’est pas une URL valide.
                  </p>
                )}

                {isReviewLink && !resolvingMap && (
                  <div className="mt-2 p-2.5 bg-green-50 border border-green-100 rounded-lg">
                    <p className="text-[11px] text-green-700 flex items-center gap-1.5">
                      <Check className="w-3.5 h-3.5" />
                      Lien direct « Rédiger un avis » détecté
                    </p>
                  </div>
                )}

                {isShortLink && !resolvingMap && (
                  <p className="text-[11px] text-amber-600 mt-1 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    Lien Google Maps détecté — résolution automatique…
                  </p>
                )}

                <p className="text-[11px] text-slate-400 mt-2 leading-snug">
                  Collez le lien Google Maps de votre établissement,
                  par exemple :
                  <br />
                  <code className="text-[10px] break-all">
                    https://maps.app.goo.gl/...
                  </code>
                  <br />
                  Tapio va tenter d’identifier l’établissement et de
                  générer le lien direct permettant au client de rédiger
                  son avis Google.
                </p>
              </div>

              {/* Note + nombre d'avis */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-navy mb-1">
                    Note
                  </label>

                  <input
                    type="text"
                    inputMode="decimal"
                    value={page.rating}
                    onChange={(event) =>
                      setPage((previous) => ({
                        ...previous,
                        rating: event.target.value,
                      }))
                    }
                    className={inputCls}
                    placeholder="4.8"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-navy mb-1">
                    Nb d'avis
                  </label>

                  <input
                    type="text"
                    inputMode="numeric"
                    value={page.reviewCount}
                    onChange={(event) =>
                      setPage((previous) => ({
                        ...previous,
                        reviewCount: event.target.value,
                      }))
                    }
                    placeholder="128"
                    className={inputCls}
                  />
                </div>
              </div>

              {/* Texte du bouton */}
              <div>
                <label className="block text-xs font-semibold text-navy mb-1">
                  Texte du bouton
                </label>

                <input
                  type="text"
                  value={page.ctaLabel}
                  onChange={(event) =>
                    setPage((previous) => ({
                      ...previous,
                      ctaLabel: event.target.value,
                    }))
                  }
                  className={inputCls}
                />
              </div>

              {/* Texte d'invitation */}
              <div>
                <label className="block text-xs font-semibold text-navy mb-1">
                  Texte d'invitation (modèle Carte NFC)
                </label>

                <input
                  type="text"
                  value={page.tapText}
                  onChange={(event) =>
                    setPage((previous) => ({
                      ...previous,
                      tapText: event.target.value,
                    }))
                  }
                  className={inputCls}
                />

                <p className="text-[11px] text-slate-400 mt-1">
                  Le mot « Google » s'affiche en dessous.
                </p>
              </div>
            </>
          )}

          {/* Onglet design */}
          {activeTab === 'design' && (
            <>
              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                Modèle
              </h3>

              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-2 gap-2">
                {REVIEW_DESIGNS.map((design) => (
                  <button
                    key={design.id}
                    onClick={() => chooseDesign(design)}
                    className={`text-left p-3 rounded-xl border-2 transition-colors ${
                      settings.design === design.id ||
                      (!settings.design && design.id === 'simple')
                        ? 'border-[#006b5f] bg-[#006b5f]/5'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <div
                      className="h-10 rounded-lg mb-2 border border-black/5"
                      style={{ background: design.swatch }}
                    />

                    <div className="text-xs font-bold text-navy">
                      {design.name}
                    </div>

                    <div className="text-[10px] text-slate-400 mt-0.5 leading-snug">
                      {design.description}
                    </div>
                  </button>
                ))}
              </div>

              {currentDesign.accent && (
                <ColorInput
                  label="Couleur d'accent du modèle"
                  value={settings.accentColor || currentDesign.accent}
                  onChange={(value) =>
                    updateSettings('accentColor', value)
                  }
                />
              )}

              {currentDesign.id !== 'simple' && (
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.showQr !== false}
                    onChange={(event) =>
                      updateSettings('showQr', event.target.checked)
                    }
                    className="w-4 h-4 rounded accent-[#006b5f]"
                  />

                  <span className="text-xs font-semibold text-navy">
                    Afficher le QR code
                  </span>
                </label>
              )}

              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider pt-3">
                Fond de la page
              </h3>

              <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-4 gap-2">
                {THEMES.map((theme) => (
                  <button
                    key={theme.id}
                    onClick={() => updateSettings('theme', theme.id)}
                    className={`aspect-square rounded-xl border-2 ${
                      settings.theme === theme.id
                        ? 'border-[#006b5f]'
                        : 'border-transparent'
                    }`}
                    style={{
                      background:
                        theme.bgType === 'gradient'
                          ? `linear-gradient(135deg, ${theme.bg}, ${theme.bg2})`
                          : theme.bg,
                    }}
                    title={theme.name}
                    aria-label={`Choisir le thème ${theme.name}`}
                  />
                ))}
              </div>

              {(currentDesign.id === 'neon' ||
                currentDesign.id === 'poster') && (
                <p className="text-[11px] text-slate-400">
                  Ce modèle utilise sa propre couleur de fond.
                </p>
              )}

              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider pt-3">
                Police
              </h3>

              <select
                value={settings.font}
                onChange={(event) =>
                  updateSettings('font', event.target.value)
                }
                className={inputCls}
              >
                {FONTS.map((font) => (
                  <option key={font.id} value={font.id}>
                    {font.name}
                  </option>
                ))}
              </select>
            </>
          )}
        </div>
      </aside>

      {/* ═══ APERÇU ═══ */}
      <main className={`${mobileView === 'edit' ? 'hidden' : 'flex'} lg:flex flex-1 min-w-0 lg:ml-[400px] items-center justify-center px-3 py-4 lg:py-10 pb-[calc(4.5rem+env(safe-area-inset-bottom))] lg:pb-10`}>
        <div className="w-full max-w-[340px] h-[clamp(420px,calc(100dvh-12rem),700px)] lg:h-[700px] rounded-[2rem] sm:rounded-[2.5rem] border-8 border-slate-900 overflow-hidden shadow-2xl relative bg-white">
          <div className="w-full h-full overflow-y-auto">
            <GoogleReviewRenderer
              page={{
                title: page.title,
                avatar: page.logo,
                settings: buildSettings(),
              }}
              preview
            />
          </div>
        </div>
      </main>

      {/* ═══ BARRE DU BAS (< lg) : Éditer / Aperçu / Enregistrer ═══ */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-30 bg-white border-t border-slate-100 pb-[env(safe-area-inset-bottom)]">
        <div className="h-14 grid grid-cols-3 items-stretch">
          <button
            onClick={() => setMobileView('edit')}
            className={`flex flex-col items-center justify-center gap-0.5 text-[11px] font-semibold ${
              mobileView === 'edit' ? 'text-[#006b5f]' : 'text-slate-500'
            }`}
          >
            <Layers className="w-5 h-5" />
            Éditer
          </button>

          <button
            onClick={() => setMobileView('preview')}
            className={`flex flex-col items-center justify-center gap-0.5 text-[11px] font-semibold ${
              mobileView === 'preview' ? 'text-[#006b5f]' : 'text-slate-500'
            }`}
          >
            <Eye className="w-5 h-5" />
            Aperçu
          </button>

          <button
            onClick={handleSave}
            disabled={isSaving || resolvingMap || uploadingLogo}
            className="flex flex-col items-center justify-center gap-0.5 text-[11px] font-semibold text-white bg-[#006b5f] active:bg-[#005a50] disabled:opacity-60"
          >
            {isSaving ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Save className="w-5 h-5" />
            )}
            Enregistrer
          </button>
        </div>
      </nav>

      {/* ═══ QR CODE ═══ */}
      {showQrModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowQrModal(false)}
        >
          <div
            className="bg-white rounded-2xl p-6 max-w-xs w-full text-center"
            onClick={(event) => event.stopPropagation()}
          >
            <h3 className="text-sm font-bold text-navy mb-4">
              QR Code de la page avis
            </h3>

            <div className="flex justify-center mb-4 p-4 bg-white border border-slate-100 rounded-xl">
              <QRCodeSVG
                value={publicUrl}
                size={180}
                level="M"
                includeMargin
              />
            </div>

            <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-lg text-xs mb-3">
              <span className="truncate flex-1 text-slate-500">
                {publicUrl}
              </span>

              <button
                onClick={copyLink}
                title="Copier"
                aria-label="Copier le lien public"
              >
                {copied ? (
                  <Check className="w-3.5 h-3.5 text-teal-600" />
                ) : (
                  <Copy className="w-3.5 h-3.5 text-slate-400" />
                )}
              </button>
            </div>

            <button
              onClick={() => setShowQrModal(false)}
              className="w-full py-2 bg-[#006b5f] text-white rounded-lg text-sm font-semibold"
            >
              Fermer
            </button>
          </div>
        </div>
      )}
    </div>
  )
}